/**
 * **Settings › Product › Visit kinds** (visits contract §4.5.1, Board 21).
 *
 * One switch per kind, and the five business types as starting points. A
 * starting point sets the switches in the form and nothing else: nothing is
 * saved until Save, and a set of switches that matches no starting point
 * saves as `custom`, with no chip lit (there is no sixth chip). `Work` is
 * always on — a company that does no work has nothing to quote — and the
 * server refuses it off too (422).
 *
 * Switching a kind off takes it out of every Kind-of-visit menu and changes
 * nothing that already exists: a visit of that kind keeps it, shows it and
 * bills as before. The warn line under an off kind says how many visits on
 * open quotes are in that position, so nobody switches one off thinking it
 * will rewrite them.
 *
 * `GET`/`PATCH /settings/visit-kinds` is the one reader and writer; on save
 * this drops `useEnabledVisitKinds()`'s cache so the next menu reads the new
 * set.
 */
import { useCallback, useEffect, useState } from 'react'
import {
  VISIT_KIND_PRESETS, VISIT_ROLES, businessTypeOf, can,
  type BusinessType, type MeResponse, type VisitRole,
} from '@rowted/shared'
import { cn } from '../lib/cn.js'
import { request } from '../lib/api.js'
import { usePhone } from '../lib/use-narrow.js'
import { useSettingsBar } from '../settings/settings-bar.js'
import { Band, Button, EmptyState, Switch } from '../components/primitives.js'
import { IconWarn } from '../frame/icons.js'
import { VISIT_KIND_MENU, VISIT_KINDS_PAGE } from '../builder/copy.js'
import { forgetEnabledVisitKinds, visitKindGlyph } from '../builder/fields/kind-field.js'

interface Payload {
  businessType: BusinessType
  kinds: VisitRole[]
  usage: Record<VisitRole, { visits: number; onOpenQuotes: number }>
}

export function VisitKindsSettingsPage({ me }: { me: MeResponse }) {
  const phone = usePhone()
  const canEdit = can(me.permissions, 'company_settings', 'edit')
  const [saved, setSaved] = useState<Payload | null>(null)
  const [kinds, setKinds] = useState<VisitRole[]>([])
  /** The chip somebody pressed — kept while the switches still match it. */
  const [chosen, setChosen] = useState<BusinessType>('all')
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      const body = await request<Payload>('/settings/visit-kinds')
      setSaved(body)
      setKinds(body.kinds)
      setChosen(body.businessType)
    } catch (error) {
      setProblem(error instanceof Error ? error.message : 'Could not load visit kinds.')
    }
  }, [])
  useEffect(() => { void load() }, [load])

  const lit = businessTypeOf(kinds, chosen)
  const dirty = saved !== null && (saved.kinds.join(',') !== kinds.join(',') || saved.businessType !== lit)

  const save = async () => {
    setBusy(true)
    setProblem(null)
    try {
      const body = await request<Payload>('/settings/visit-kinds',
        { method: 'PATCH', body: { kinds, businessType: lit } })
      setSaved(body)
      setKinds(body.kinds)
      setChosen(body.businessType)
      forgetEnabledVisitKinds()
    } catch (error) {
      setProblem(error instanceof Error ? error.message : 'Could not save.')
    } finally {
      setBusy(false)
    }
  }

  const cancel = () => {
    if (!saved) return
    setKinds(saved.kinds)
    setChosen(saved.businessType)
  }

  const toggle = (role: VisitRole, on: boolean) => {
    if (role === 'work') return
    const next = VISIT_ROLES.filter((one) => (one === role ? on : kinds.includes(one)))
    setKinds(next)
  }

  // Board 21's bar: `Settings / Product / Visit kinds`.
  const barActions = useSettingsBar({
    title: VISIT_KINDS_PAGE.title, crumbs: [{ label: 'Product', to: '/settings' }],
  })

  if (!saved) {
    return (
      <div className="p-5">
        {barActions}
        {problem
          ? <EmptyState title="Not available" body={problem} />
          : <p className="m-0 text-body text-text-muted">Loading…</p>}
      </div>
    )
  }

  const chips = (
    <div className={cn('flex items-center gap-2',
      phone ? 'no-scrollbar -mx-4 overflow-x-auto px-4' : 'flex-wrap')}>
      {!phone && <span className="mr-1 text-[12px] text-text-muted">{VISIT_KINDS_PAGE.startFrom}</span>}
      {VISIT_KIND_PRESETS.map((preset) => {
        const on = lit === preset.key
        return (
          <button
            key={preset.key}
            type="button"
            aria-pressed={on}
            disabled={!canEdit}
            onClick={() => { setChosen(preset.key); setKinds([...preset.kinds]) }}
            className={cn('inline-flex shrink-0 items-center whitespace-nowrap rounded-full border font-semibold',
              phone ? 'h-9 px-4 text-[14px]' : 'h-[30px] px-[13px] text-[12.5px]',
              on ? 'border-text bg-text text-surface' : 'border-line bg-surface text-text hover:border-line-strong')}
          >
            {VISIT_KINDS_PAGE.presets[preset.key]}
          </button>
        )
      })}
    </div>
  )

  const rows = VISIT_ROLES.map((role) => {
    const on = kinds.includes(role)
    const use = saved.usage[role] ?? { visits: 0, onOpenQuotes: 0 }
    const locked = role === 'work'
    const tile = (
      <span aria-hidden className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] border',
        on ? 'border-accent-line bg-accent-soft text-accent' : 'border-line bg-line-soft text-text-faint')}>
        {visitKindGlyph(role, 15)}
      </span>
    )
    const control = (
      <Switch
        checked={on}
        label={VISIT_KIND_MENU[role].label}
        hideLabel
        disabled={locked || !canEdit}
        onChange={(next) => toggle(role, next)}
      />
    )
    if (phone) {
      return (
        <div key={role} data-kind-row={role}
          className="flex min-h-[58px] items-center gap-3 border-b border-line-faint bg-surface px-4 py-2.5">
          {tile}
          <span className="flex min-w-0 flex-1 flex-col gap-px">
            <b className={cn('text-[15px] font-bold', on ? 'text-text' : 'text-text-muted')}>
              {VISIT_KIND_MENU[role].label}
            </b>
            <span className="text-[12.5px] text-text-muted">
              {locked ? VISIT_KINDS_PAGE.alwaysOn : VISIT_KIND_MENU[role].help}
            </span>
          </span>
          {control}
        </div>
      )
    }
    return (
      <div key={role} data-kind-row={role} className="border-t border-line-faint py-3">
        <div className="flex items-center gap-3.5">
          {tile}
          <span className="flex min-w-0 flex-1 flex-col gap-px">
            <b className={cn('text-[14px] font-bold', on ? 'text-text' : 'text-text-muted')}>
              {VISIT_KIND_MENU[role].label}
            </b>
            <span className="text-[12.5px] text-text-muted">{VISIT_KIND_MENU[role].help}</span>
          </span>
          <span className={cn('tnum w-[120px] text-right text-[12px]',
            use.visits > 0 ? 'text-text-muted' : 'text-text-faint')}>
            {use.visits > 0 ? VISIT_KINDS_PAGE.visits(use.visits) : VISIT_KINDS_PAGE.notUsed}
          </span>
          <span className="flex w-[150px] items-center justify-end gap-2.5">
            {locked && <span className="text-[12px] text-text-faint">{VISIT_KINDS_PAGE.alwaysOn}</span>}
            {control}
          </span>
        </div>
        {!on && use.onOpenQuotes > 0 && (
          <p className="m-0 mt-1.5 flex items-center gap-1.5 pl-[50px] text-[12px] text-warn">
            <IconWarn size={13} />
            {VISIT_KINDS_PAGE.inUseOff(use.onOpenQuotes)}
          </p>
        )}
      </div>
    )
  })

  if (phone) {
    return (
      <div className="flex min-h-full flex-col bg-bg pb-[96px]">
        {barActions}
        <p className="m-0 px-4 pb-3 pt-3 text-[14px] leading-[1.4] text-text-muted">{VISIT_KINDS_PAGE.phoneSub}</p>
        <div className="px-4 pb-3">{chips}</div>
        {problem && <div className="px-4 pb-3"><Band tone="amber" icon={<IconWarn size={15} />}>{problem}</Band></div>}
        <div className="border-t border-line-faint">{rows}</div>
        {canEdit && (
          <div className="fixed inset-x-0 bottom-0 z-40 border-t border-line-faint bg-surface px-4 pb-[28px] pt-3">
            <Button variant="primary" className="h-12 w-full text-[16px]" disabled={!dirty || busy}
              onClick={() => void save()}>
              {VISIT_KINDS_PAGE.save}
            </Button>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="p-5">
      {barActions}
      <section className="max-w-[800px] px-1.5 pb-1.5 pt-0">
        <h2 className="m-0 text-[14px] font-bold text-text">{VISIT_KINDS_PAGE.heading}</h2>
        <p className="m-0 mb-3.5 mt-1 text-[12.5px] text-text-muted">{VISIT_KINDS_PAGE.sub}</p>
        {chips}
        <p className="m-0 my-2 text-[11.5px] text-text-faint">
          {VISIT_KINDS_PAGE.presetNote[0]}<b className="font-semibold">{VISIT_KINDS_PAGE.presetNote[1]}</b>
          {VISIT_KINDS_PAGE.presetNote[2]}
        </p>
        {problem && <Band tone="amber" icon={<IconWarn size={15} />}>{problem}</Band>}
        <div className="mt-1.5">{rows}</div>
        <div className="mt-1 flex items-center gap-2.5 border-t border-line-faint pt-4">
          <p className="m-0 max-w-[520px] flex-1 text-[12px] leading-[1.4] text-text-faint">
            {VISIT_KINDS_PAGE.footer}
          </p>
          {canEdit && (
            <>
              <Button disabled={!dirty || busy} onClick={cancel}>{VISIT_KINDS_PAGE.cancel}</Button>
              <Button variant="primary" disabled={!dirty || busy} onClick={() => void save()}>
                {VISIT_KINDS_PAGE.save}
              </Button>
            </>
          )}
        </div>
      </section>
    </div>
  )
}
